import { api, type ExerciseStep, type QuizStep, type Step } from "../api.ts";
import { h, icon } from "../dom.ts";
import { appendToNotes, createDrawer } from "../panels.ts";
import { navigate } from "../router.ts";
import { TerminalSession, type TerminalStatus } from "../terminal.ts";

const STEP_LABELS = { lesson: "Notion", exercise: "Exercice", quiz: "Quiz" } as const;
const STEP_ICONS = { lesson: "book", exercise: "flask", quiz: "quiz" } as const;

/** Message texte (éventuellement issu du conteneur) : échappé, `code` mis en forme. */
function formatMessage(text: string): HTMLElement {
  const div = document.createElement("div");
  text.split(/(`[^`]+`)/).forEach((part) => {
    div.append(part.startsWith("`") && part.endsWith("`") && part.length > 2 ? h("code", {}, part.slice(1, -1)) : part);
  });
  return div;
}

/** Extrait le texte d'un fragment HTML (indice) pour l'ajouter aux notes. */
function htmlToText(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent?.trim() ?? "";
}

export async function renderModule(root: HTMLElement, moduleId: string): Promise<() => void> {
  const [{ module, completed }, programme] = await Promise.all([api.module(moduleId), api.programme()]);
  document.title = `${module.title} · Linux 101`;
  const done = new Set(completed);

  const params = new URLSearchParams(location.search);
  const requested = module.steps.findIndex((s) => s.id === params.get("etape"));
  const firstTodo = module.steps.findIndex((s) => !done.has(s.id));
  let current = requested >= 0 ? requested : firstTodo >= 0 ? firstTodo : 0;

  const drawer = createDrawer(programme.modules, module.order);

  // En-tête.
  const progressBar = h("span");
  const progressText = h("span", { class: "muted small" });
  const header = h(
    "header",
    { class: "topbar" },
    h(
      "a",
      { class: "brand", href: "/", onclick: (e: Event) => (e.preventDefault(), navigate("/")) },
      h("span", { class: "brand-mark" }, icon("terminal", 18)),
      "Linux 101",
    ),
    h("span", { class: "topbar-sep" }, "/"),
    h("span", { class: "topbar-title" }, `Module ${module.order} · ${module.title}`),
    h("div", { class: "topbar-progress" }, h("div", { class: "progress" }, progressBar), progressText),
    h(
      "div",
      { class: "topbar-actions" },
      h("button", { class: "btn btn-ghost", onclick: () => drawer.toggle("cheatsheet") }, icon("list", 16), h("span", {}, "Aide-mémoire")),
      h("button", { class: "btn btn-ghost", onclick: () => drawer.toggle("notes") }, icon("note", 16), h("span", {}, "Mes notes")),
    ),
  );

  // Navigation entre étapes.
  const stepper = h("nav", { class: "stepper", "aria-label": "Étapes du module" });
  const stepContent = h("article", { class: "step-content" });
  const prevBtn = h("button", { class: "btn btn-ghost", onclick: () => go(current - 1) }, icon("left", 16), "Précédent");
  const nextBtn = h("button", { class: "btn btn-primary", onclick: () => onNext() });
  const lessonPane = h(
    "section",
    { class: "lesson-pane" },
    stepper,
    stepContent,
    h("footer", { class: "step-footer" }, prevBtn, nextBtn),
  );

  // Terminal.
  const statusDot = h("span", { class: "status-dot" });
  const statusText = h("span", {}, "");
  const termHost = h("div", { class: "terminal-host" });
  const overlay = h("div", { class: "terminal-overlay", hidden: true });
  const resetBtn = h("button", { class: "btn btn-dark", onclick: () => void resetEnvironment() }, icon("reset", 15), "Réinitialiser");
  const terminalPane = h(
    "section",
    { class: "terminal-pane", "aria-label": "Terminal" },
    h(
      "div",
      { class: "terminal-toolbar" },
      h("div", { class: "terminal-status" }, statusDot, h("span", { class: "terminal-host-name" }, "apprenant@altair-srv01"), statusText),
      resetBtn,
    ),
    h("div", { class: "terminal-frame" }, termHost, overlay),
  );

  root.replaceChildren(
    h("div", { class: "module-layout" }, header, h("main", { class: "workspace" }, lessonPane, module.hasEnvironment ? terminalPane : null), drawer.element),
  );

  let terminal: TerminalSession | null = null;
  if (module.hasEnvironment) {
    terminal = new TerminalSession(termHost, module.id, setStatus);
    void terminal.connect();
  }

  function setStatus(status: TerminalStatus, detail?: string): void {
    statusDot.dataset.status = status;
    statusText.textContent = {
      preparing: "Préparation de l'environnement…",
      connecting: "Connexion…",
      connected: "",
      disconnected: "Déconnecté",
      error: "Indisponible",
    }[status];
    overlay.hidden = status === "connected";
    if (status === "connected") return;
    const busy = status === "preparing" || status === "connecting";
    const message = busy
      ? status === "preparing"
        ? "Préparation de votre environnement Linux…"
        : "Connexion au terminal…"
      : detail ?? "Le terminal est déconnecté.";
    overlay.replaceChildren(
      ...[
        busy ? h("div", { class: "spinner" }) : null,
        h("p", {}, message),
        busy ? null : h("button", { class: "btn btn-primary", onclick: () => void terminal?.connect() }, "Se reconnecter"),
      ].filter((el) => el !== null),
    );
  }

  async function resetEnvironment(): Promise<void> {
    const ok = confirm(
      "Réinitialiser l'environnement ?\n\nLe serveur sera remis dans son état de départ : toutes les modifications faites dans le terminal seront perdues. Votre progression et vos notes sont conservées.",
    );
    if (!ok || !terminal) return;
    resetBtn.setAttribute("disabled", "");
    try {
      await api.resetEnvironment(module.id);
      await terminal.connect();
    } finally {
      resetBtn.removeAttribute("disabled");
    }
  }

  function markDone(stepId: string): void {
    done.add(stepId);
    renderChrome();
  }

  function renderChrome(): void {
    const count = module.steps.filter((s) => done.has(s.id)).length;
    progressBar.style.width = `${(count / module.steps.length) * 100}%`;
    progressText.textContent = `${count} / ${module.steps.length}`;

    stepper.replaceChildren(
      ...module.steps.map((step, i) =>
        h(
          "button",
          {
            class: `step-dot step-${step.type}${done.has(step.id) ? " is-done" : ""}${i === current ? " is-current" : ""}`,
            title: `${i + 1}. ${STEP_LABELS[step.type]} : ${step.title}`,
            "aria-label": `Étape ${i + 1} : ${step.title}${done.has(step.id) ? " (terminée)" : ""}`,
            "aria-current": i === current ? "step" : null,
            onclick: () => go(i),
          },
          done.has(step.id) ? icon("check", 12) : icon(STEP_ICONS[step.type], 12),
        ),
      ),
    );

    prevBtn.toggleAttribute("disabled", current === 0);
    const last = current === module.steps.length - 1;
    nextBtn.replaceChildren(last ? "Terminer le module" : "Suivant", icon(last ? "check" : "right", 16));
  }

  function go(index: number): void {
    if (index < 0 || index >= module.steps.length) return;
    current = index;
    const url = new URL(location.href);
    url.searchParams.set("etape", module.steps[index]!.id);
    history.replaceState(null, "", url);
    renderStep(module.steps[index]!);
    renderChrome();
    stepContent.scrollTo({ top: 0 });
    lessonPane.scrollTo({ top: 0 });
  }

  async function onNext(): Promise<void> {
    const step = module.steps[current]!;
    if (step.type === "lesson" && !done.has(step.id)) {
      await api.complete(module.id, step.id);
      markDone(step.id);
    }
    if (current < module.steps.length - 1) go(current + 1);
    else renderEnd();
  }

  function stepHeading(step: Step, index: number) {
    return h(
      "div",
      { class: "step-heading" },
      h(
        "p",
        { class: `step-kicker kicker-${step.type}` },
        icon(STEP_ICONS[step.type], 14),
        `${STEP_LABELS[step.type]} · Étape ${index + 1} sur ${module.steps.length}`,
        done.has(step.id) ? h("span", { class: "badge badge-done" }, icon("check", 12), "Validé") : null,
      ),
      h("h1", {}, step.title),
    );
  }

  function renderStep(step: Step): void {
    const heading = stepHeading(step, current);
    if (step.type === "lesson") {
      stepContent.replaceChildren(heading, h("div", { class: "prose", html: step.html }));
    } else if (step.type === "exercise") {
      stepContent.replaceChildren(heading, renderExercise(step));
    } else {
      stepContent.replaceChildren(heading, renderQuiz(step));
    }
  }

  function renderExercise(step: ExerciseStep): HTMLElement {
    const feedback = h("div", { class: "feedback", "aria-live": "polite" });
    const hints = h("div", { class: "hints" });
    let revealed = 0;

    const checkBtn = h("button", { class: "btn btn-primary" }, icon("check", 16), "Vérifier");
    const hintBtn = h("button", { class: "btn btn-secondary" });
    const updateHintBtn = () => {
      hintBtn.replaceChildren(
        icon("bulb", 16),
        revealed === step.hintCount - 1 ? "Voir la solution" : `Indice ${revealed + 1} / ${step.hintCount}`,
      );
      hintBtn.toggleAttribute("disabled", revealed >= step.hintCount);
      if (revealed >= step.hintCount) hintBtn.replaceChildren(icon("bulb", 16), "Tous les indices affichés");
    };
    updateHintBtn();

    checkBtn.addEventListener("click", async () => {
      checkBtn.setAttribute("disabled", "");
      feedback.className = "feedback is-pending";
      feedback.replaceChildren(h("span", { class: "spinner spinner-sm" }), "Vérification en cours…");
      try {
        const result = await api.check(module.id, step.id);
        feedback.className = `feedback ${result.ok ? "is-success" : "is-failure"}`;
        feedback.replaceChildren(icon(result.ok ? "check" : "x", 18), formatMessage(result.message));
        if (result.ok && !done.has(step.id)) {
          markDone(step.id);
          stepContent.querySelector(".step-heading")?.replaceWith(stepHeading(step, current));
        }
      } catch (err) {
        feedback.className = "feedback is-failure";
        feedback.replaceChildren(icon("x", 18), err instanceof Error ? err.message : String(err));
      } finally {
        checkBtn.removeAttribute("disabled");
      }
    });

    hintBtn.addEventListener("click", async () => {
      hintBtn.setAttribute("disabled", "");
      const index = revealed;
      const { html } = await api.hint(module.id, step.id, index);
      revealed++;
      const isSolution = index === step.hintCount - 1;
      hints.append(
        h(
          "div",
          { class: `hint${isSolution ? " is-solution" : ""}` },
          h("p", { class: "hint-title" }, isSolution ? "Solution" : `Indice ${index + 1}`),
          h("div", { class: "prose", html }),
          h(
            "button",
            {
              class: "link-btn",
              onclick: () => void appendToNotes(`[${module.title} · ${step.title}] ${htmlToText(html)}`),
            },
            icon("plus", 14),
            "Ajouter à mes notes",
          ),
        ),
      );
      updateHintBtn();
    });

    return h(
      "div",
      { class: "exercise" },
      h("div", { class: "prose", html: step.html }),
      h("p", { class: "muted small exercise-tip" }, icon("terminal", 14), "Travaillez dans le terminal, puis cliquez sur « Vérifier »."),
      h("div", { class: "exercise-actions" }, checkBtn, hintBtn),
      feedback,
      hints,
    );
  }

  function renderQuiz(step: QuizStep): HTMLElement {
    let answered = 0;
    let correct = 0;
    const summary = h("div", { class: "feedback", hidden: true, "aria-live": "polite" });

    const questions = step.questions.map((q, qi) => {
      const explanation = h("div", { class: "quiz-explanation", hidden: true });
      const buttons = q.choices.map((choice, ci) =>
        h("button", { class: "quiz-choice", html: choice, onclick: () => answer(ci) }),
      );
      function answer(ci: number): void {
        buttons.forEach((b, i) => {
          b.setAttribute("disabled", "");
          if (i === q.answer) b.classList.add("is-correct");
          else if (i === ci) b.classList.add("is-wrong");
        });
        const ok = ci === q.answer;
        explanation.hidden = false;
        explanation.className = `quiz-explanation ${ok ? "is-success" : "is-failure"}`;
        explanation.replaceChildren(
          h("p", { class: "quiz-verdict" }, icon(ok ? "check" : "x", 16), ok ? "Bonne réponse" : "Pas tout à fait"),
          h("div", { class: "prose", html: q.explanation }),
        );
        answered++;
        if (ok) correct++;
        if (answered === step.questions.length) void finish();
      }
      return h(
        "fieldset",
        { class: "quiz-question" },
        h("legend", {}, h("span", { class: "quiz-num" }, `${qi + 1}`), h("div", { class: "prose", html: q.question })),
        h("div", { class: "quiz-choices" }, buttons),
        explanation,
      );
    });

    async function finish(): Promise<void> {
      summary.hidden = false;
      const perfect = correct === step.questions.length;
      summary.className = `feedback ${perfect ? "is-success" : "is-info"}`;
      summary.replaceChildren(
        icon(perfect ? "check" : "bulb", 18),
        h(
          "div",
          {},
          `${correct} bonne(s) réponse(s) sur ${step.questions.length}. `,
          perfect ? "Parfait." : "Relisez les explications ci-dessus : elles seront utiles pour la suite.",
        ),
      );
      if (!done.has(step.id)) {
        await api.complete(module.id, step.id);
        markDone(step.id);
      }
    }

    return h("div", { class: "quiz" }, questions, summary);
  }

  function renderEnd(): void {
    const remaining = module.steps.filter((s) => !done.has(s.id));
    const next = programme.modules.find((m) => m.order > module.order && m.available);
    stepContent.replaceChildren(
      h(
        "div",
        { class: "module-end" },
        h("div", { class: "module-end-icon" }, icon(remaining.length ? "list" : "check", 28)),
        h("h1", {}, remaining.length ? "Fin du module" : "Module terminé"),
        remaining.length
          ? h(
              "div",
              {},
              h("p", {}, `Il reste ${remaining.length} étape(s) non validée(s) :`),
              h(
                "ul",
                { class: "remaining" },
                remaining.map((s) =>
                  h("li", {}, h("button", { class: "link-btn", onclick: () => go(module.steps.indexOf(s)) }, `${STEP_LABELS[s.type]} : ${s.title}`)),
                ),
              ),
            )
          : h("p", { class: "lead" }, `Bravo, vous avez validé toutes les étapes du module « ${module.title} ».`),
        h("p", { class: "muted" }, "Pensez à relire l'aide-mémoire et à compléter vos notes avec ce que vous retenez."),
        h(
          "div",
          { class: "exercise-actions" },
          h("button", { class: "btn btn-secondary", onclick: () => drawer.open("cheatsheet") }, icon("list", 16), "Relire l'aide-mémoire"),
          next
            ? h("button", { class: "btn btn-primary", onclick: () => navigate(`/modules/${next.id}`) }, `Module suivant : ${next.title}`, icon("right", 16))
            : h("button", { class: "btn btn-primary", onclick: () => navigate("/") }, "Retour au programme"),
        ),
      ),
    );
  }

  go(current);
  return () => terminal?.dispose();
}
